/**
 * UPT-2096 (P1-a, §4.5) — avertissements calculés dans le navigateur. Ils ne
 * servent QU'à l'aperçu avant import et au dépôt de démonstration ; après le
 * 201, seuls comptent les avertissements du serveur. Règles UNIQUES du
 * contrat (`PROTOTYPE_PII_*`), appliquées à l'identique par le serveur
 * (Java) ; vecteurs de référence : `pii-vectors.json`. Jamais d'empreinte
 * d'une valeur personnelle.
 */
import {
  PROTOTYPE_PII_IGNORED_DOMAINS,
  PROTOTYPE_PII_IGNORED_TLDS,
  PROTOTYPE_PII_PATTERNS,
  type PrototypeDocumentRole,
  type PrototypeWarningCode,
} from '../data/contracts';

/** Forme de détection (miroir de PrototypeText.scanForm, rest §4.7.8). */
export const scanForm = (value: string): string => {
  if (value === null || value === undefined) return value;
  let out = '';
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (
      (code >= 0x200b && code <= 0x200d) || code === 0x2060 || code === 0xfeff ||
      code === 0x200e || code === 0x200f || code === 0x061c ||
      (code >= 0x202a && code <= 0x202e) || (code >= 0x2066 && code <= 0x2069)
    ) {
      continue;
    }
    if (/\p{Cc}/u.test(char) && char !== '\n') {
      out += ' ';
    } else {
      out += char;
    }
  }
  return out.normalize('NFC');
};

const maskEmail = (value: string): string => {
  const at = value.lastIndexOf('@');
  const local = value.slice(0, at);
  const host = value.slice(at + 1);
  const labels = host.split('.');
  return `${local.charAt(0)}•••@${host.charAt(0)}•••.${labels[labels.length - 1]}`;
};

const maskAvs = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  return `756.••••.••••.${digits.slice(-2)}`;
};

const maskIban = (value: string): string => {
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  return `${cleaned.slice(0, 4)} ${'•'.repeat(cleaned.length - 8)} ${cleaned.slice(-4)}`;
};

const maskPhone = (value: string): string => {
  const digitCount = (value.match(/\d/g) ?? []).length;
  const keepFrom = digitCount - 2;
  let seen = 0;
  let out = '';
  for (const char of value) {
    if (/\d/.test(char)) {
      out += seen >= keepFrom ? char : '•';
      seen += 1;
    } else {
      out += char;
    }
  }
  return out;
};

/** Contrôle EAN-13 (préfixe 756 imposé par le motif). */
const isValidAvs = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 13) return false;
  let sum = 0;
  for (let i = 0; i < 12; i += 1) {
    const digit = digits.charCodeAt(i) - 48;
    sum += i % 2 === 0 ? digit : digit * 3;
  }
  return (10 - (sum % 10)) % 10 === digits.charCodeAt(12) - 48;
};

/** Contrôle mod-97 (ISO 13616) sur la valeur sans espaces, en majuscules. */
const isValidIban = (value: string): boolean => {
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  if (cleaned.length < 5 || cleaned.length > 34) return false;
  const rearranged = cleaned.slice(4) + cleaned.slice(0, 4);
  let remainder = 0;
  for (const char of rearranged) {
    const text = /[A-Z]/.test(char) ? String(char.charCodeAt(0) - 55) : char;
    for (const digit of text) {
      remainder = (remainder * 10 + (digit.charCodeAt(0) - 48)) % 97;
    }
  }
  return remainder === 1;
};

const isIgnoredEmail = (value: string): boolean => {
  const host = value.slice(value.lastIndexOf('@') + 1).toLowerCase();
  for (const domain of PROTOTYPE_PII_IGNORED_DOMAINS) {
    if (host === domain || host.endsWith(`.${domain}`)) return true;
  }
  const tld = host.slice(host.lastIndexOf('.') + 1);
  return (PROTOTYPE_PII_IGNORED_TLDS as readonly string[]).includes(tld);
};

export interface PersonalDataScanHit {
  code: PrototypeWarningCode;
  count: number;
  example: string;
}

interface Span {
  start: number;
  end: number;
}

/**
 * Données personnelles d'un texte en forme de détection : ordre IBAN, AVS,
 * courriel, téléphone (international CH, E.164, national CH) ; une
 * correspondance qui chevauche une portion déjà reconnue est ignorée, et
 * toute correspondance examinée est marquée reconnue ; résultat trié par code.
 */
export const scanPersonalData = (text: string): PersonalDataScanHit[] => {
  if (!text) return [];
  const consumed: Span[] = [];
  const overlaps = (start: number, end: number): boolean =>
    consumed.some((span) => start < span.end && end > span.start);
  const hits = new Map<PrototypeWarningCode, { count: number; example: string }>();
  const record = (code: PrototypeWarningCode, example: string): void => {
    const entry = hits.get(code);
    if (entry) {
      entry.count += 1;
    } else {
      hits.set(code, { count: 1, example });
    }
  };
  const each = (
    pattern: string,
    accept: (value: string) => { code: PrototypeWarningCode; example: string } | null
  ): void => {
    const regex = new RegExp(pattern, 'g');
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      if (match[0].length === 0) {
        regex.lastIndex += 1;
        continue;
      }
      if (overlaps(match.index, match.index + match[0].length)) continue;
      const masked = accept(match[0]);
      if (masked !== null) {
        record(masked.code, masked.example);
      }
      consumed.push({ start: match.index, end: match.index + match[0].length });
    }
  };

  each(PROTOTYPE_PII_PATTERNS.iban, (value) =>
    isValidIban(value) ? { code: 'pii.iban', example: maskIban(value) } : null);
  each(PROTOTYPE_PII_PATTERNS.avs, (value) =>
    isValidAvs(value) ? { code: 'pii.avs', example: maskAvs(value) } : null);
  each(PROTOTYPE_PII_PATTERNS.email, (value) =>
    isIgnoredEmail(value) ? null : { code: 'pii.email', example: maskEmail(value) });
  for (const pattern of [
    PROTOTYPE_PII_PATTERNS.phoneChInternational,
    PROTOTYPE_PII_PATTERNS.phoneE164,
    PROTOTYPE_PII_PATTERNS.phoneChNational,
  ]) {
    each(pattern, (value) => ({ code: 'pii.phone', example: maskPhone(value) }));
  }

  return [...hits.entries()]
    .map(([code, entry]) => ({ code, count: entry.count, example: entry.example }))
    .sort((a, b) => a.code.localeCompare(b.code));
};

/** Élément masqué par un style en ligne display:none / visibility:hidden. */
const HIDDEN_STYLE = /(?:^|;)\s*(?:display\s*:\s*none|visibility\s*:\s*hidden)\s*(?:!important\s*)?(?=;|$)/i;

const isMaskedElement = (element: Element): boolean => {
  if (element.hasAttribute('hidden')) return true;
  if ((element.getAttribute('aria-hidden') ?? '').trim().toLowerCase() === 'true') return true;
  return HIDDEN_STYLE.test(element.getAttribute('style') ?? '');
};

const SKIP_TAGS = new Set(['script', 'style', 'template', 'noscript']);

/**
 * Texte analysé d'un document inerte (miroir de rest §5.3) : titre ; nœuds
 * texte du body hors script/style (cachés compris, joints par une espace) ;
 * attributs placeholder, value, title, alt, aria-label, content ; scripts
 * sans src et styles ; commentaires ; parties jointes par « \n ».
 */
export const analysedText = (doc: Document): string => {
  const parts: string[] = [];
  if (doc.title) parts.push(doc.title);
  const bodyTexts: string[] = [];
  const walker = doc.createTreeWalker(doc.body ?? doc.documentElement, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const parent = (node as Text).parentElement;
    if (parent && !SKIP_TAGS.has(parent.tagName.toLowerCase())) {
      const data = (node as Text).data;
      if (data.trim()) bodyTexts.push(data);
    }
    node = walker.nextNode();
  }
  if (bodyTexts.length > 0) parts.push(bodyTexts.join(' '));
  const attributes = ['placeholder', 'value', 'title', 'alt', 'aria-label', 'content'];
  doc.querySelectorAll('*').forEach((element) => {
    for (const attribute of attributes) {
      const value = element.getAttribute(attribute);
      if (value) parts.push(value);
    }
  });
  doc.querySelectorAll('script:not([src])').forEach((script) => {
    if (script.textContent) parts.push(script.textContent);
  });
  doc.querySelectorAll('style').forEach((style) => {
    if (style.textContent) parts.push(style.textContent);
  });
  const comments = doc.createTreeWalker(doc, NodeFilter.SHOW_COMMENT);
  let comment = comments.nextNode();
  while (comment) {
    parts.push((comment as Comment).data);
    comment = comments.nextNode();
  }
  return parts.join('\n');
};

/** Hôtes distincts (minuscules, ordre du document) des ressources externes — external.resources. */
export const externalResourceHosts = (doc: Document): string[] => {
  const hosts: string[] = [];
  doc.querySelectorAll('script[src], link[href], img[src], iframe[src], source[src], video[src], audio[src]')
    .forEach((element) => {
      const raw = (element.getAttribute('src') ?? element.getAttribute('href') ?? '').trim();
      const value = raw.startsWith('//') ? `https:${raw}` : raw;
      if (!value.startsWith('http://') && !value.startsWith('https://')) return;
      try {
        const host = new URL(value).host.toLowerCase();
        if (host && !hosts.includes(host)) hosts.push(host);
      } catch {
        // URL illisible : ignorée.
      }
    });
  return hosts;
};

const NETWORK_TOKENS = ['fetch(', 'XMLHttpRequest', 'WebSocket', 'EventSource', 'sendBeacon'];

const countOccurrences = (text: string, token: string): number => {
  let count = 0;
  let from = 0;
  for (;;) {
    const index = text.indexOf(token, from);
    if (index < 0) return count;
    count += 1;
    from = index + token.length;
  }
};

const countComments = (doc: Document): number => {
  const walker = doc.createTreeWalker(doc, NodeFilter.SHOW_COMMENT);
  let count = 0;
  while (walker.nextNode()) count += 1;
  return count;
};

/** Éléments masqués les plus externes au texte non vide (hidden.invisible) — même règle que rest §5.2. */
const countInvisible = (doc: Document): number => {
  let count = 0;
  const visit = (element: Element): void => {
    if (SKIP_TAGS.has(element.tagName.toLowerCase())) return;
    if (isMaskedElement(element)) {
      const text = (element.textContent ?? '').replace(/\s+/g, ' ').trim();
      if (text) count += 1;
      return;
    }
    for (const child of Array.from(element.children)) visit(child);
  };
  if (doc.body) visit(doc.body);
  return count;
};

// eslint-disable-next-line no-misleading-character-class -- caractères de largeur nulle visés un par un
const ZERO_WIDTH = /[\u200B\u200C\u200D\u2060\uFEFF]/g;

export interface PreviewHtml {
  doc: Document;
  text: string;
  fallback: boolean;
}

/** L2 : document Markdown de l'aperçu ; seules les `notes` sont scannées (le `prompt` l'est comme prompt). */
export interface PreviewMarkdown {
  fileName: string;
  text: string;
  role: PrototypeDocumentRole;
}

export interface PreviewWarningsInput {
  html: PreviewHtml | PreviewHtml[] | null;
  prompt: string | null;
  images: { resized: string | null }[];
  markdowns?: PreviewMarkdown[];
}

export interface PreviewWarning {
  code: PrototypeWarningCode;
  count: number;
  example: string | null;
  source: 'html' | 'prompt' | 'image' | 'markdown';
  /** L2 : rang de la source HTML (source `html`). */
  sourceIndex?: number;
  /** L2 : nom du document (source `markdown`). */
  name?: string;
}

/** Avertissements d'UNE source HTML. */
const htmlSourceWarnings = (html: PreviewHtml, sourceIndex: number | undefined): PreviewWarning[] => {
  const out: PreviewWarning[] = [];
  // Un `PreviewHtml` seul (forme P1-a) ne porte pas de rang ; une liste porte le rang de chaque source.
  const rank = sourceIndex === undefined ? {} : { sourceIndex };
  const { doc, fallback } = html;
  const scan = analysedText(doc);
  for (const hit of scanPersonalData(scanForm(scan))) {
    out.push({ code: hit.code, count: hit.count, example: hit.example, source: 'html', ...rank });
  }
  const comments = countComments(doc);
  if (comments > 0) out.push({ code: 'hidden.comments', count: comments, example: null, source: 'html', ...rank });
  const invisible = countInvisible(doc);
  if (invisible > 0) out.push({ code: 'hidden.invisible', count: invisible, example: null, source: 'html', ...rank });
  const zeroWidth = (scan.match(ZERO_WIDTH) ?? []).length;
  if (zeroWidth > 0) out.push({ code: 'hidden.zero_width', count: zeroWidth, example: null, source: 'html', ...rank });
  const hosts = externalResourceHosts(doc);
  if (hosts.length > 0) {
    out.push({ code: 'external.resources', count: hosts.length, example: hosts[0], source: 'html', ...rank });
  }
  let network = 0;
  let firstToken: string | null = null;
  let firstIndex = Number.MAX_SAFE_INTEGER;
  const handlers: string[] = [];
  doc.querySelectorAll('script:not([src])').forEach((script) => handlers.push(script.textContent ?? ''));
  doc.querySelectorAll('*').forEach((element) => {
    for (const attribute of Array.from(element.attributes)) {
      if (attribute.name.toLowerCase().startsWith('on')) handlers.push(attribute.value);
    }
  });
  const joined = handlers.join('\n');
  for (const token of NETWORK_TOKENS) {
    network += countOccurrences(joined, token);
    const index = joined.indexOf(token);
    if (index >= 0 && index < firstIndex) {
      firstIndex = index;
      firstToken = token;
    }
  }
  if (network > 0) out.push({ code: 'external.network', count: network, example: firstToken, source: 'html', ...rank });
  if (fallback) out.push({ code: 'html.encoding_fallback', count: 1, example: null, source: 'html', ...rank });
  return out;
};

/** Avertissements de l'aperçu avant import (jamais envoyés, sauf les deux codes navigateur). */
export const computePreviewWarnings = (input: PreviewWarningsInput): PreviewWarning[] => {
  const warnings: PreviewWarning[] = [];
  if (Array.isArray(input.html)) {
    input.html.forEach((html, sourceIndex) => warnings.push(...htmlSourceWarnings(html, sourceIndex)));
  } else if (input.html) {
    warnings.push(...htmlSourceWarnings(input.html, undefined));
  }
  for (const markdown of input.markdowns ?? []) {
    if (markdown.role !== 'notes') continue;
    for (const hit of scanPersonalData(scanForm(markdown.text))) {
      warnings.push({ code: hit.code, count: hit.count, example: hit.example, source: 'markdown', name: markdown.fileName });
    }
  }
  if (input.prompt) {
    for (const hit of scanPersonalData(scanForm(input.prompt))) {
      warnings.push({ code: hit.code, count: hit.count, example: hit.example, source: 'prompt' });
    }
  }
  if (input.images.length > 0) {
    warnings.push({ code: 'image.unscanned', count: input.images.length, example: null, source: 'image' });
    const resized = input.images.filter((image) => image.resized !== null);
    if (resized.length > 0) {
      warnings.push({ code: 'image.resized', count: resized.length, example: resized[0].resized, source: 'image' });
    }
  }
  const sourceOrder = { html: 0, prompt: 1, markdown: 2, image: 3 } as const;
  return warnings
    .filter((warning) => warning.count > 0)
    .sort(
      (a, b) =>
        sourceOrder[a.source] - sourceOrder[b.source] ||
        (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0) ||
        (a.name ?? '').localeCompare(b.name ?? '') ||
        a.code.localeCompare(b.code)
    );
};
